/**
 * Phase 19 (2026-07-05) — Manage Permissions drawer.
 *
 * Opened from a per-row "Manage Permissions" button on UsersPage. Shows three
 * tabs: Role Defaults · User Overrides · Effective. The Overrides tab uses a
 * 3-state toggle (Inherit / Allow / Deny) and on Save PATCHes
 * `/api/permissions/user/{id}` with the diff (Inherit → null deletes
 * override).
 */
import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { Loader2, Save } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import api, { formatApiError } from "@/lib/api";
import { PermissionTree } from "@/components/PermissionTree";
import { ROLE_LABELS } from "@/lib/permissions";

const TEMPLATES = {
  "All Allow": (keys) => Object.fromEntries(keys.map((k) => [k, true])),
  "View Only": (keys) =>
    Object.fromEntries(keys.map((k) => [k, k.endsWith(".view")])),
  "No Access": (keys) => Object.fromEntries(keys.map((k) => [k, false])),
  "Use Role Default": () => ({}), // clear all overrides
};

export default function ManagePermissionsDrawer({ user, open, onClose, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [keys, setKeys] = useState([]);
  const [roleDefaults, setRoleDefaults] = useState({});
  const [overrides, setOverrides] = useState({}); // dict key → bool
  const [draft, setDraft] = useState({}); // working overrides (modifiable)

  useEffect(() => {
    if (!open || !user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const [{ data: ks }, { data: u }] = await Promise.all([
          api.get("/permissions/keys"),
          api.get(`/permissions/user/${user.id}`),
        ]);
        if (cancelled) return;
        setKeys(ks.keys);
        setRoleDefaults(u.role_defaults || {});
        setOverrides(u.permission_overrides || {});
        setDraft(u.permission_overrides || {});
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, user]);

  const effective = useMemo(() => {
    const out = {};
    for (const k of keys) {
      if (k in draft) out[k] = !!draft[k];
      else out[k] = !!roleDefaults[k];
    }
    return out;
  }, [keys, draft, roleDefaults]);

  const dirty = useMemo(() => {
    const o = overrides || {};
    const d = draft || {};
    const ok = new Set([...Object.keys(o), ...Object.keys(d)]);
    for (const k of ok) {
      if (o[k] !== d[k]) return true;
    }
    return false;
  }, [overrides, draft]);

  const applyTemplate = (name) => {
    const tpl = TEMPLATES[name];
    if (!tpl) return;
    setDraft(tpl(keys));
    toast.info(`Applied template: ${name}`);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build the payload: union of old + new keys. Keys missing from
      // draft → send `null` to drop the override. Keys in draft → bool.
      const payload = {};
      const all = new Set([...Object.keys(overrides), ...Object.keys(draft)]);
      for (const k of all) {
        if (k in draft) payload[k] = draft[k];
        else payload[k] = null; // drop
      }
      await api.patch(`/permissions/user/${user.id}`, {
        permission_overrides: payload,
        reason: "manage_permissions_drawer",
      });
      toast.success("Permissions updated");
      onSaved?.();
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!user) return null;
  const customCount = Object.keys(draft || {}).length;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="max-w-4xl"
        data-testid="manage-permissions-drawer"
      >
        <DialogHeader>
          <DialogTitle>Manage permissions — {user.full_name}</DialogTitle>
          <DialogDescription>
            {user.email} ·{" "}
            <Badge variant="outline">{ROLE_LABELS[user.role] || user.role}</Badge>{" "}
            · {user.is_active ? "Active" : "Inactive"}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex h-64 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-[#4FA3D1]" />
          </div>
        ) : (
          <Tabs defaultValue="overrides" className="w-full">
            <TabsList>
              <TabsTrigger value="role" data-testid="tab-role">
                Role Defaults
              </TabsTrigger>
              <TabsTrigger value="overrides" data-testid="tab-overrides">
                User Overrides{" "}
                <Badge variant="outline" className="ml-1.5">
                  {customCount}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="effective" data-testid="tab-effective">
                Effective
              </TabsTrigger>
            </TabsList>

            <TabsContent value="role" className="mt-3">
              <div className="mb-2 text-xs text-slate-500">
                Read-only view of the permissions granted by the user&apos;s role.
                To change role defaults, edit the role on{" "}
                <strong>/settings/role-permissions</strong>.
              </div>
              <PermissionTree
                keys={keys}
                values={roleDefaults}
                threeState={false}
                onChange={() => {}}
              />
            </TabsContent>

            <TabsContent value="overrides" className="mt-3">
              <div className="mb-3 flex flex-wrap gap-2">
                {Object.keys(TEMPLATES).map((name) => (
                  <Button
                    key={name}
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => applyTemplate(name)}
                    data-testid={`tpl-${name.replace(/\s+/g, "-").toLowerCase()}`}
                  >
                    {name}
                  </Button>
                ))}
              </div>
              <PermissionTree
                keys={keys}
                values={draft}
                roleDefaults={roleDefaults}
                threeState={true}
                onChange={(k, v) => {
                  const next = { ...draft };
                  if (v === undefined) delete next[k];
                  else next[k] = v;
                  setDraft(next);
                }}
              />
            </TabsContent>

            <TabsContent value="effective" className="mt-3">
              <div className="mb-2 text-xs text-slate-500">
                What the user can actually do, after overrides are applied.
              </div>
              <PermissionTree
                keys={keys}
                values={effective}
                threeState={false}
                onChange={() => {}}
              />
            </TabsContent>
          </Tabs>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!dirty || saving}
            className="bg-[#4FA3D1] hover:bg-[#2F86B5] text-white"
            data-testid="save-permissions"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
